<template>
    <div class="flex flex-col justify-center items-center gap-12">
        <!-- Filters -->
        <div class="flex flex-wrap gap-4 items-end w-full">
            <!-- Search Filter -->
            <div class="flex-1 min-w-64">
                <IconField icon-position="left">
                    <InputIcon>
                        <i class="pi pi-search" />
                    </InputIcon>
                    <InputText v-model="searchQuery" placeholder="Titel oder Beschreibung" class="w-full" />
                </IconField>
            </div>

            <!-- Tags Filter -->
            <div class="flex-1 min-w-20">
                <MultiSelect filter v-model="selectedTags" :options="availableTags" placeholder="Tags" class="w-full"
                    show-clear />
            </div>

            <!-- Releases Filter -->
            <div class="flex-1 min-w-10">
                <MultiSelect filter v-model="selectedReleases" :options="availableReleases" placeholder="Releases"
                    class="w-full" show-clear />
            </div>

            <!-- Date Range Filter -->
            <div class="flex-1">
                <DatePicker v-model="selectedDateRange" selectionMode="range" view="month" dateFormat="mm/yy"
                    placeholder="nach Datum" icon="pi pi-calendar" class="w-full" showClear />
            </div>
        </div>

        <!-- Articles Grid -->
        <div class="w-full grid grid-cols-1 md:grid-cols-2 gap-8">
            <template v-for="(item, index) of filteredArticles" :key="index">
                <Card class="cursor-pointer h-full" @click="navigateTo(item.url)">
                    <template #content>
                        <div class="flex flex-col gap-4 h-full">
                            <div class="flex justify-between items-center">
                                <span>{{ item.type }}</span>
                                <div class="flex gap-4">
                                    <div> {{ item.release }}</div>
                                    <div class="flex items-center gap-2">
                                        <i class="pi pi-hourglass" />
                                        <span>{{ item.readTime }}</span>
                                    </div>

                                </div>

                            </div>
                            <Content :path="item.description" />
                            <div class="flex-1" />
                            <div class="flex justify-between items-end gap-4">
                                <div class="flex flex-wrap items-center gap-2">
                                    <template v-for="(tag, tagIndex) of item.tags" :key="tagIndex">
                                        <Tag :value="tag" />
                                    </template>
                                </div>
                                <Button icon="pi pi-arrow-right" severity="secondary" text rounded />
                            </div>
                        </div>
                    </template>
                </Card>
            </template>
        </div>

        <!-- No Results Message -->
        <div v-if="filteredArticles.length === 0" class="no-results-message">
            <i class="pi pi-inbox text-4xl mb-4 block" />
            <p>No articles match your filters. Try adjusting your search criteria.</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import Card from 'primevue/card';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import DatePicker from 'primevue/datepicker';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';

const router = useRouter();

interface Article {
    type: string;
    readTime: string;
    title: string;
    tags: string[];
    url: string;
    date?: Date;
    release?: string;
    description?: string;
}

const props = defineProps<{
  issues: Article[]
}>()

const articles = ref<Article[]>(props.issues);

// Filter states
const searchQuery = ref('');
const selectedTags = ref<string[]>([]);
const selectedReleases = ref<string[]>([]);
const selectedDateRange = ref<Date[] | null>(null);

// Available tags from all articles
const availableTags = computed(() => {
    const tags = new Set<string>();
    articles.value.forEach(article => {
        article.tags.forEach(tag => tags.add(tag));
    });
    return Array.from(tags).sort();
});

// Available releases from all articles
const availableReleases = computed(() => {
    const releases = new Set<string>();
    articles.value.forEach(article => {
        if (article.release) {
            releases.add(article.release);
        }
    });
    return Array.from(releases).sort();
});

// Filtered articles based on all filters
const filteredArticles = computed(() => {
    return articles.value.filter(article => {
        // Search filter (title and description)
        const searchLower = searchQuery.value.toLowerCase();
        const matchesSearch = searchLower === '' ||
            article.title.toLowerCase().includes(searchLower) ||
            (article.description?.toLowerCase().includes(searchLower) ?? false);

        // Tags filter
        const matchesTags = selectedTags.value.length === 0 ||
            selectedTags.value.some(tag => article.tags.includes(tag));

        // Releases filter
        const matchesReleases = selectedReleases.value.length === 0 ||
            selectedReleases.value.includes(article.release ?? '');

        // Date range filter (matches articles within the selected date range)
        const matchesDate = !selectedDateRange.value || selectedDateRange.value.length === 0 ||
            (article.date &&
                selectedDateRange.value[0] &&
                (!selectedDateRange.value[1] || true) &&
                new Date(article.date) >= new Date(selectedDateRange.value[0]) &&
                (!selectedDateRange.value[1] || new Date(article.date) <= new Date(selectedDateRange.value[1]))
            );

        return matchesSearch && matchesTags && matchesReleases && matchesDate;
    });
});

const navigateTo = (url: string) => {
    router.push(url);
};
</script>
<style scoped>
.no-results-message {
  text-align: center;
  color: var(--p-text-color-secondary);
  padding: 2rem 0;
}
</style>